<template>
  <div :class="[$shTemplateStyle.headerItemContainer, $style.container]">
    <div :class="$style.topContainer">
      <div :class="$style.phaseText">{{ phaseLabel[phase] }}</div>
      <div :class="$shTemplateStyle.infoContainer">
        <div :class="$shTemplateStyle.label">{{ $t("time") }}</div>
        <div :class="$shTemplateStyle.text">
          {{ formattedTime }}
        </div>
      </div>
    </div>

    <!-- Smooth Progress Bar -->
    <div :class="$style.progressBarContainer">
      <div
        :class="$style.progressBar"
        :style="{
          width: progressBarWidth + '%',
          backgroundColor: progressColor,
        }"
      ></div>
    </div>
  </div>
</template>

<script>
import styles from "@/css/PhaseTimerBox.module.css";
import secondaryHeaderTemplateStyles from "@/css/SecondaryHeaderTemplate.module.css";

export default {
  components: {},
  name: "PhaseTimerBox",
  props: {
    // duration: { type: Number, required: true }, // Total duration in seconds
    phase: {
      type: String,
      required: true,
    },
    readingTime: {
      type: Number,
      required: true,
    },
    discussionTime: {
      type: Number,
      required: true,
    },
    phaseStartedAt: {
      type: [Object, String, Number, Date],
      default: null,
    },
    phaseEndsAt: {
      type: [Object, String, Number, Date],
      default: null,
    },
    phaseDurationSeconds: {
      type: Number,
      default: null,
    },
    // Replays pass their playback clock (ms); the timer then follows it instead of the real time.
    nowMs: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      remainingTime: 0, // Convert seconds to milliseconds
      totalPhaseDuration: 0,
      startTime: null,
      animationFrameId: null,
      timerEndedEmitted: false,
      // phaseLabel: {
      //   JOIN: "Lobby",
      //   READ: $t("game-details"), //FIX
      //   PRELIMINARY_VOTE: "Preliminary Voting",
      //   DISCUSS: "Discussion Phase",
      //   VOTE: "Voting for Saboteur",
      //   REVEAL: "Reveal Phase",
      //   DEBRIEF: "Debrief Phase",
      //   // "DOWNLOAD": "",
      //   // "END":
      // },
    };
  },
  computed: {
    $style() {
      return styles;
    },
    // $globalStyle() {
    //   return globalStyles;
    // },
    $shTemplateStyle() {
      return secondaryHeaderTemplateStyles;
    },
    formattedTime() {
      const totalSeconds = Math.max(Math.ceil(this.remainingTime / 1000), 0);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
    },
    progressBarWidth() {
      if (!this.totalPhaseDuration) return 0;
      return Math.min(
        Math.max((this.remainingTime / (this.totalPhaseDuration * 1000)) * 100, 0),
        100
      ); // Smooth scaling
    },
    progressColor() {
      if (!this.totalPhaseDuration) return "hsl(0, 100%, 35%)";

      // Gradually change from Green (120) to Red (0) as time decreases
      const ratio = Math.min(
        Math.max(this.remainingTime / (this.totalPhaseDuration * 1000), 0),
        1
      );
      const hue = ratio * 120;
      return `hsl(${hue}, 100%, 35%)`;
    },
    phaseDurations() {
      return {
        // JOIN: 60,
        READ: Number(this.readingTime || 0) * 60,
        PRELIMINARY_VOTE: 420,
        DISCUSS: Number(this.discussionTime || 0) * 60,
        VOTE: 420,
        REVEAL: 120,
        DEBRIEF: 600,
      };
    },
    phaseLabel() {
      return {
        JOIN: this.$t("lobby-phase"),
        READ: this.$t("read-phase"),
        PRELIMINARY_VOTE: this.$t("preliminary-vote-phase"),
        DISCUSS: this.$t("discuss-phase"),
        VOTE: this.$t("voting-phase"),
        REVEAL: this.$t("reveal-phase"),
        DEBRIEF: this.$t("debrief-phase"),
      };
    },
  },
  methods: {
    timestampToMillis(value) {
      if (!value) return null;
      if (typeof value.toMillis === "function") return value.toMillis();
      if (value instanceof Date) return value.getTime();
      if (typeof value.seconds === "number") {
        return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1000000);
      }
      if (typeof value._seconds === "number") {
        return value._seconds * 1000 + Math.floor((value._nanoseconds || 0) / 1000000);
      }
      if (typeof value === "number") return value < 1000000000000 ? value * 1000 : value;
      if (typeof value === "string") {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? null : parsed;
      }

      return null;
    },
    authoritativeDurationSeconds() {
      const explicitDuration = Number(this.phaseDurationSeconds || 0);
      if (explicitDuration > 0) return explicitDuration;

      const startMs = this.timestampToMillis(this.phaseStartedAt);
      const endMs = this.timestampToMillis(this.phaseEndsAt);
      if (startMs && endMs && endMs > startMs) {
        return Math.ceil((endMs - startMs) / 1000);
      }

      return 0;
    },
    emitTimerEndedOnce() {
      if (this.timerEndedEmitted) return;
      this.timerEndedEmitted = true;
      this.$emit("timerEnded"); // Notify parent when time ends
    },
    syncToClock() {
      const startMs = this.timestampToMillis(this.phaseStartedAt);
      const total = this.authoritativeDurationSeconds() || this.phaseDurations[this.phase] || 120;
      const endMs = this.timestampToMillis(this.phaseEndsAt) ?? (startMs != null ? startMs + total * 1000 : null);
      this.totalPhaseDuration = total;
      this.remainingTime = endMs != null ? Math.max(endMs - this.nowMs, 0) : total * 1000;
    },
    startCountdown(timestamp) {
      const authoritativeEndMs = this.timestampToMillis(this.phaseEndsAt);
      const authoritativeDuration = this.authoritativeDurationSeconds();

      if (authoritativeEndMs && authoritativeDuration > 0) {
        this.totalPhaseDuration = authoritativeDuration;
        this.remainingTime = Math.max(authoritativeEndMs - Date.now(), 0);
      } else {
        if (!this.startTime) this.startTime = timestamp;
        const elapsed = timestamp - this.startTime;

        this.remainingTime = Math.max(this.totalPhaseDuration * 1000 - elapsed, 0);
      }

      if (this.remainingTime > 0) {
        this.animationFrameId = requestAnimationFrame(this.startCountdown);
      } else {
        this.emitTimerEndedOnce();
      }
    },
    restartTimer() {
      // Cancel previous timer if exists
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }

      if (this.nowMs != null) {
        this.syncToClock();
        return;
      }

      const authoritativeEndMs = this.timestampToMillis(this.phaseEndsAt);
      const authoritativeDuration = this.authoritativeDurationSeconds();

      this.timerEndedEmitted = false;
      this.startTime = null;

      if (authoritativeEndMs && authoritativeDuration > 0) {
        this.totalPhaseDuration = authoritativeDuration;
        this.remainingTime = Math.max(authoritativeEndMs - Date.now(), 0);
      } else {
        // Set new duration based on phase
        this.totalPhaseDuration = this.phaseDurations[this.phase] || 120; // Default to 120 seconds if phase not found
        this.remainingTime = this.totalPhaseDuration * 1000;
      }

      // console.log(`🔄 Timer restarted for phase: ${this.phase} with duration: ${this.totalPhaseDuration} seconds`);

      // Start the countdown
      this.animationFrameId = requestAnimationFrame(this.startCountdown);
    },
  },
  mounted() {
    // this.animationFrameId = requestAnimationFrame(this.startCountdown);
    // console.log(`duration inside ${this.phase} phase timer: ${this.duration}`)
  },
  watch: {
    // Restart timer on phase change
    phase: {
      immediate: true,
      handler() {
        this.restartTimer();
      },
    },
    phaseStartedAt() {
      this.restartTimer();
    },
    phaseEndsAt() {
      this.restartTimer();
    },
    phaseDurationSeconds() {
      this.restartTimer();
    },
    readingTime() {
      this.restartTimer();
    },
    discussionTime() {
      this.restartTimer();
    },
    nowMs(value) {
      if (value != null) this.syncToClock();
    },
  },
  beforeUnmount() {
    cancelAnimationFrame(this.animationFrameId);
  },
};
</script>

<style scoped>
.container {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #ededed;
  border-radius: 12px;
}

</style>
